import React, { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DashboardCard from '../components/DashboardCard';
import DataTable from '../components/DataTable';

const DailySalesClosing = ({ isEndOfDay = true }) => {
    const { authFetch, selectedDepo, user } = useContext(AppContext);
    const { t } = useTranslation();
    
    const [activeTab, setActiveTab] = useState('Current'); // 'Current' | 'History'
    
    // Filters for Current Day
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [cashierId, setCashierId] = useState('');
    const [cashiersList, setCashiersList] = useState([]);
    const [accounts, setAccounts] = useState([]);
    
    // Data State
    const [loading, setLoading] = useState(true);
    const [closingData, setClosingData] = useState(null); // The summary data
    const [dayStatus, setDayStatus] = useState('Open'); // 'Open' or 'Closed'
    const [actualCash, setActualCash] = useState('');
    const [differenceReason, setDifferenceReason] = useState('');
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Expense State
    const [expenses, setExpenses] = useState([]);
    const [isExpenseOpen, setIsExpenseOpen] = useState(false);
    const [expenseCashier, setExpenseCashier] = useState('');
    const [expenseRows, setExpenseRows] = useState([{ category: '', amount: '', description: '' }]);

    // History details modal state
    const [viewingHistoryRecord, setViewingHistoryRecord] = useState(null);
    const [historyDetails, setHistoryDetails] = useState(null);
    const [historyExpenses, setHistoryExpenses] = useState([]);
    const [historyPurchases, setHistoryPurchases] = useState([]);
    const [editingHistoryExpense, setEditingHistoryExpense] = useState(null);

    // Fetch cashiers for filter dropdown
    useEffect(() => {
        authFetch('/api/users')
            .then(res => res.json())
            .then(data => {
                if(Array.isArray(data)) {
                    setCashiersList(data.filter(u => u.role === 'Cashier' || u.role === 'Admin' || u.role === 'Manager'));
                }
            })
            .catch(console.error);
            
        authFetch('/api/accounts')
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) setAccounts(data);
            })
            .catch(console.error);
    }, [authFetch]);

    useEffect(() => {
        if (isEndOfDay) {
            setCashierId('');
        }
        if (activeTab === 'Current') {
            fetchDailyStats();
            fetchExpenses();
        } else {
            fetchHistory();
        }
    }, [date, selectedDepo, cashierId, activeTab, authFetch, isEndOfDay]);

    const fetchExpenses = async () => {
        try {
            const warehouseId = selectedDepo === 'Global' ? (user?.depo_id || 1) : selectedDepo;
            const res = await authFetch(`/api/expenses?date=${date}&depo_id=${warehouseId}`);
            const data = await res.json();
            setExpenses(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchDailyStats = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                date,
                warehouse_id: selectedDepo === 'Global' ? (user?.depo_id || 1) : selectedDepo,
                cashier_id: cashierId
            });
            const res = await authFetch(`/api/closure?${params.toString()}`);
            const data = await res.json();
            
            setDayStatus(data.status); // 'Open' or 'Closed'
            setClosingData(data.data);
            
            if (data.status === 'Closed') {
                setActualCash(data.data.actual_cash || '');
                setDifferenceReason(data.data.difference_reason || '');
            } else {
                setActualCash('');
                setDifferenceReason('');
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        setLoadingHistory(true);
        try {
            const res = await authFetch('/api/closure?action=history');
            const data = await res.json();
            if(Array.isArray(data)) {
                setHistory(data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleSaveExpense = async (e) => {
        e.preventDefault();
        const validRows = expenseRows.filter(r => r.category && r.amount);
        if (validRows.length === 0) {
            return alert(t('Please add at least one valid expense row.'));
        }
        try {
            const warehouseId = selectedDepo === 'Global' ? (user?.depo_id || 1) : selectedDepo;
            const res = await authFetch('/api/expenses', {
                method: 'POST',
                body: JSON.stringify({ 
                    depo_id: warehouseId, 
                    daily_closing_id: closingData?.id, 
                    date: date,
                    cashier_id: expenseCashier || null,
                    expenses: validRows
                })
            });

            if (res.ok) {
                setExpenseRows([{ category: '', amount: '', description: '' }]);
                setExpenseCashier('');
                setIsExpenseOpen(false); // CLOSE THE MODAL
                fetchExpenses();
                fetchDailyStats(); // refresh totals
            } else {
                alert(t('Failed to save expenses'));
            }
        } catch (err) {
            alert(t('Network error'));
        }
    };
    
    const handleDeleteExpense = async (id) => {
        if(!window.confirm(t('Are you sure you want to delete this expense?'))) return;
        try {
            const res = await authFetch(`/api/expenses/${id}`, { method: 'DELETE' });
            if (res.ok) {
                fetchExpenses();
                fetchDailyStats();
            }
        } catch(e) { }
    };

    const handleCloseDay = async () => {
        if (!window.confirm(t('Are you sure you want to officially close this day? This will lock all transactions.'))) {
            return;
        }

        const expectedCash = closingData?.expected_cash || 0;
        const actCash = parseFloat(actualCash || 0);
        const cashDiff = actCash - expectedCash;

        if (Math.abs(cashDiff) > 0 && !differenceReason) {
            return alert(t('Please provide a reason for the cash difference.'));
        }

        try {
            const payload = {
                ...closingData,
                closure_date: date,
                warehouse_id: selectedDepo === 'Global' ? (user?.depo_id || 1) : selectedDepo,
                cashier_id: cashierId || null,
                actual_cash: actCash,
                cash_difference: cashDiff,
                difference_reason: differenceReason
            };

            const res = await authFetch('/api/closure', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                alert(t('Day successfully closed!'));
                fetchDailyStats();
            } else {
                const err = await res.json();
                alert(err.message || t('Failed to close day.'));
            }
        } catch (err) {
            alert(t('Network error'));
        }
    };

    const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'CFA' }).format(val || 0);

    // Print Logic 
    const handlePrint = (type) => {
        let printContents = '';
        
        if (type === 'report') {
            printContents = `
                <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
                    <h2 style="text-align: center; border-bottom: 2px solid #ddd; padding-bottom: 10px; color: #333;">DAILY SALES REPORT</h2>
                    
                    <div className="w-full overflow-x-auto max-w-full">
<table style="width: 100%; margin-bottom: 30px; font-size: 14px; color: #555;">
                        <tr>
                            <td><strong>Date:</strong> ${date}</td>
                            <td style="text-align: right;"><strong>Warehouse:</strong> ${selectedDepo}</td>
                        </tr>
                        <tr>
                            <td><strong>Cashier:</strong> ${cashierId ? cashiersList.find(c => c.id == cashierId)?.name : 'All Cashiers'}</td>
                            <td style="text-align: right;"><strong>Status:</strong> ${dayStatus}</td>
                        </tr>
                    </table>
</div>
                    
                    <div className="w-full overflow-x-auto max-w-full">
<table style="width: 100%; border-collapse: collapse; margin-bottom: 40px; font-size: 15px;">
                        <thead>
                            <tr style="background-color: #f8f9fa; border-bottom: 2px solid #cbd5e1;">
                                <th style="padding: 12px; text-align: left; color: #334155;">Category</th>
                                <th style="padding: 12px; text-align: right; color: #334155;">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style="border-bottom: 1px solid #e2e8f0;">
                                <td style="padding: 12px; color: #475569;">Walk-in Sales (Cash)</td>
                                <td style="padding: 12px; text-align: right; font-weight: 600;">CFA ${parseFloat(closingData?.walkin_total || 0).toFixed(2)}</td>
                            </tr>
                            <tr style="border-bottom: 1px solid #e2e8f0;">
                                <td style="padding: 12px; color: #475569;">Account Credit (Invoiced)</td>
                                <td style="padding: 12px; text-align: right; font-weight: 600;">CFA ${parseFloat(closingData?.account_credit_total || 0).toFixed(2)}</td>
                            </tr>
                            <tr style="border-bottom: 1px solid #e2e8f0;">
                                <td style="padding: 12px; color: #475569;">Card Payments</td>
                                <td style="padding: 12px; text-align: right; font-weight: 600;">CFA ${parseFloat(closingData?.card_total || 0).toFixed(2)}</td>
                            </tr>
                            <tr style="border-bottom: 1px solid #e2e8f0;">
                                <td style="padding: 12px; color: #ef4444;">Returned Items</td>
                                <td style="padding: 12px; text-align: right; font-weight: 600; color: #ef4444;">- CFA ${parseFloat(closingData?.return_total || 0).toFixed(2)}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr style="background-color: #f1f5f9;">
                                <td style="padding: 16px; border-top: 2px solid #cbd5e1; font-weight: bold; font-size: 1.2em; color: #1e293b;">NET SALES</td>
                                <td style="padding: 16px; text-align: right; border-top: 2px solid #cbd5e1; font-weight: bold; font-size: 1.2em; color: #1e293b;">CFA ${parseFloat(closingData?.net_sales || 0).toFixed(2)}</td>
                            </tr>
                        </tfoot>
                    </table>
</div>
                    
                    <div style="text-align: center; color: #94a3b8; font-size: 12px; margin-top: 50px;">
                        Report generated on ${new Date().toLocaleString()}
                    </div>
                </div>
            `;
        }
        
        const printWindow = window.open('', '_blank', 'width=600,height=800');
        if (printWindow) {
            printWindow.document.write(`
                <html>
                <head>
                    <title>Daily Sales Report</title>
                </head>
                <body onload="window.print(); window.close();">
                    ${printContents}
                </body>
                </html>
            `);
            printWindow.document.close();
        } else {
            alert('Please allow popups to print reports.');
        }
    };


    // Fetch history details when a record is selected
    useEffect(() => {
        if (viewingHistoryRecord) {
            const fetchAll = async () => {
                try {
                    const d = viewingHistoryRecord.closure_date;
                    const wId = viewingHistoryRecord.warehouse_id;
                    const cId = viewingHistoryRecord.cashier_id || '';
                    
                    const resCl = await authFetch(`/api/closure?date=${d}&warehouse_id=${wId}&cashier_id=${cId}`);
                    if (resCl.ok) {
                        const data = await resCl.json();
                        setHistoryDetails(data.data);
                    }
                    
                    const resExp = await authFetch(`/api/expenses?date=${d}&depo_id=${wId}`);
                    if (resExp.ok) {
                        const dataExp = await resExp.json();
                        setHistoryExpenses(Array.isArray(dataExp) ? dataExp : []);
                    }

                    const resPur = await authFetch(`/api/purchases?date=${d}`);
                    if (resPur.ok) {
                        const dataPur = await resPur.json();
                        setHistoryPurchases(Array.isArray(dataPur) ? dataPur : []);
                    }
                } catch (e) {
                    console.error("Error fetching history details:", e);
                }
            };
            fetchAll();
        }
    }, [viewingHistoryRecord]);

    const handleUpdateHistoryExpense = async (e) => {
        e.preventDefault();
        try {
            const res = await authFetch(`/api/expenses/${editingHistoryExpense.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    category: editingHistoryExpense.category,
                    amount: editingHistoryExpense.amount,
                    description: editingHistoryExpense.description
                })
            });
            if (res.ok) {
                setEditingHistoryExpense(null);
                // re-fetch data for modal
                const d = viewingHistoryRecord.closure_date;
                const wId = viewingHistoryRecord.warehouse_id;
                const cId = viewingHistoryRecord.cashier_id || '';
                
                const resCl = await authFetch(`/api/closure?date=${d}&warehouse_id=${wId}&cashier_id=${cId}`);
                if (resCl.ok) {
                    const data = await resCl.json();
                    setHistoryDetails(data.data);
                }
                const resExp = await authFetch(`/api/expenses?date=${d}&depo_id=${wId}`);
                if (resExp.ok) {
                    const dataExp = await resExp.json();
                    setHistoryExpenses(Array.isArray(dataExp) ? dataExp : []);
                }
                
                fetchHistory(); // refresh main history grid
            } else {
                alert(t('Failed to update expense'));
            }
        } catch (err) {
            alert(t('Network error'));
        }
    };

    const historyColumns = [
        { header: t('Date'), accessor: 'closure_date', render: r => new Date(r.closure_date).toLocaleDateString() },
        { header: t('Warehouse'), accessor: 'warehouse_name' },
        { header: t('Cashier'), accessor: 'cashier_name', render: r => r.cashier_name || t('All') },
        { header: t('Net Sales'), accessor: 'net_sales', render: r => <span className="font-bold">{formatCurrency(r.net_sales)}</span> },
        { header: t('Closed By'), accessor: 'closed_by_name' },
        { header: t('Status'), accessor: 'status', render: r => (
            <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">{r.status}</span>
        )},
        { 
            header: t('Action'), 
            accessor: 'id', 
            render: r => (
                <button 
                    onClick={() => {
                        setViewingHistoryRecord(r);
                        setHistoryDetails(null);
                        setHistoryExpenses([]);
                        setHistoryPurchases([]);
                    }}
                    className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-md text-xs font-bold transition-colors"
                >
                    {t('View Details')}
                </button>
            )
        }
    ];

    return (
        <div className="space-y-6 max-w-6xl mx-auto pb-12">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800 tracking-tight">
                        {isEndOfDay ? t('End of Day Closing') : t('Daily Sales Report')}
                    </h2>
                    <p className="text-gray-500 mt-1">
                        {isEndOfDay 
                            ? t('Review financial activity, verify totals, and officially close the business day.') 
                            : t('Review daily sales and cashier performance.')}
                    </p>
                </div>
                
                <div className="flex bg-gray-200 p-1 rounded-lg">
                    <button 
                        onClick={() => setActiveTab('Current')}
                        className={`px-6 py-2 rounded-md font-semibold text-sm transition ${activeTab === 'Current' ? 'bg-white shadow text-indigo-600' : 'text-gray-600 hover:text-gray-800'}`}
                    >
                        {t('Current Day')}
                    </button>
                    <button 
                        onClick={() => setActiveTab('History')}
                        className={`px-6 py-2 rounded-md font-semibold text-sm transition ${activeTab === 'History' ? 'bg-white shadow text-indigo-600' : 'text-gray-600 hover:text-gray-800'}`}
                    >
                        {t('Reports History')}
                    </button>
                </div>
            </header>

            {activeTab === 'Current' && (
                <>
                    {/* Filters */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-wrap gap-4 items-end">
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Business Date')}</label>
                            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border p-2 rounded w-full" />
                        </div>
                        {!isEndOfDay && (
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Cashier')}</label>
                                <select value={cashierId} onChange={(e) => setCashierId(e.target.value)} className="border p-2 rounded w-full min-w-[200px]">
                                    <option value="">-- {t('All Cashiers')} --</option>
                                    {cashiersList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                        )}
                        <div className="flex-1"></div>
                        <div>
                            <span className={`px-4 py-2 rounded-lg font-bold shadow-sm ${dayStatus === 'Closed' ? 'bg-red-100 text-red-800 border border-red-200' : 'bg-emerald-100 text-emerald-800 border border-emerald-200'}`}>
                                {t('Status')}: {dayStatus === 'Closed' ? t('CLOSED (Locked)') : t('OPEN')}
                            </span>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center p-10"><span className="animate-spin text-4xl">⏳</span></div>
                    ) : closingData ? (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            
                            {/* Sales Summary Breakdown */}
                            <div className="lg:col-span-2 space-y-6">
                                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                                    <div className="flex justify-between items-center mb-6">
                                        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                            <span>📊</span> {t('Sales Summary')}
                                        </h3>
                                        <button onClick={() => handlePrint('report')} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1 transition">
                                            <span>🖨️</span> {t('Print Report')}
                                        </button>
                                    </div>
                                    
                                    <div className="space-y-3">
                                        <div className="flex justify-between p-2 hover:bg-gray-50 border-b border-gray-100">
                                            <span className="text-gray-600">{t('Walk-in Sales (Cash)')}</span>
                                            <span className="font-semibold">{formatCurrency(closingData.walkin_total)}</span>
                                        </div>
                                        <div className="flex justify-between p-2 hover:bg-gray-50 border-b border-gray-100">
                                            <span className="text-gray-600">{t('Account Credit (Invoiced)')}</span>
                                            <span className="font-semibold">{formatCurrency(closingData.account_credit_total)}</span>
                                        </div>
                                        <div className="flex justify-between p-2 hover:bg-gray-50 border-b border-gray-100">
                                            <span className="text-gray-600">{t('Card Payments')}</span>
                                            <span className="font-semibold">{formatCurrency(closingData.card_total)}</span>
                                        </div>
                                        <div className="flex justify-between p-2 hover:bg-red-50 border-b border-red-100 text-red-600">
                                            <span>{t('Returns / Refunds')}</span>
                                            <span className="font-semibold">-{formatCurrency(closingData.return_total)}</span>
                                        </div>
                                        <div className="flex justify-between p-3 bg-indigo-50 text-indigo-900 rounded-lg mt-2">
                                            <span className="font-bold">{t('Net Sales')}</span>
                                            <span className="font-black text-xl">{formatCurrency(closingData.net_sales)}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="mt-6 pt-4 border-t border-gray-200">
                                        <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">{t('Outflows')}</h4>
                                        <div className="space-y-2">
                                            <div className="flex justify-between p-2 bg-orange-50 text-orange-800 rounded-lg">
                                                <span className="font-semibold">{t('Expenses (Dépenses)')}</span>
                                                <span className="font-bold">-{formatCurrency(closingData.expense_total)}</span>
                                            </div>
                                            <div className="flex justify-between p-2 bg-rose-50 text-rose-800 rounded-lg">
                                                <span className="font-semibold">{t('Purchases (Achats)')}</span>
                                                <span className="font-bold">-{formatCurrency(closingData.achat_total || 0)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Cashier Summary */}
                                {!cashierId && (
                                    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                            <span>👥</span> {t('Cashier Breakdown')}
                                        </h3>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm text-left">
                                                <thead className="bg-gray-50 text-gray-600 font-semibold uppercase text-xs">
                                                    <tr>
                                                        <th className="px-3 py-2">{t('Cashier')}</th>
                                                        <th className="px-3 py-2">{t('Invoices')}</th>
                                                        <th className="px-3 py-2">{t('Sales')}</th>
                                                        <th className="px-3 py-2">{t('Returns')}</th>
                                                        <th className="px-3 py-2">{t('Expenses')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {closingData.cashier_summary?.map((c, i) => (
                                                        <tr key={i} className="hover:bg-gray-50 cursor-pointer" onClick={() => setCashierId(c.id)}>
                                                            <td className="px-3 py-2 font-medium text-indigo-600">{c.name}</td>
                                                            <td className="px-3 py-2">{c.invoice_count}</td>
                                                            <td className="px-3 py-2 font-semibold text-emerald-600">{formatCurrency(c.total_sales)}</td>
                                                            <td className="px-3 py-2 text-red-500">{formatCurrency(c.returns)}</td>
                                                            <td className="px-3 py-2 text-orange-500">{formatCurrency(c.expenses)}</td>
                                                        </tr>
                                                    ))}
                                                    {(!closingData.cashier_summary || closingData.cashier_summary.length === 0) && (
                                                        <tr><td colSpan="5" className="text-center py-4 text-gray-500">{t('No cashier activity')}</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Expenses Summary */}
                                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm mt-6">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                            <span>💸</span> {t('Expenses (Dépenses)')}
                                        </h3>
                                        {dayStatus === 'Open' && (
                                            <button onClick={() => setIsExpenseOpen(true)} className="bg-orange-100 hover:bg-orange-200 text-orange-700 px-3 py-1.5 rounded-lg text-sm font-semibold transition">
                                                + {t('Add Expense')}
                                            </button>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        {expenses.length > 0 ? expenses.map((exp, i) => (
                                            <div key={i} className="flex justify-between items-center p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                                                <div>
                                                    <div className="font-bold text-gray-700">
                                                        {exp.category}
                                                        {exp.cashier_name && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold">{exp.cashier_name}</span>}
                                                    </div>
                                                    {exp.description && <div className="text-xs text-gray-500">{exp.description}</div>}
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <span className="font-bold text-orange-600">-{formatCurrency(exp.amount)}</span>
                                                    {dayStatus === 'Open' && (
                                                        <button onClick={() => handleDeleteExpense(exp.id)} className="text-red-400 hover:text-red-600 text-lg font-bold px-2">×</button>
                                                    )}
                                                </div>
                                            </div>
                                        )) : (
                                            <div className="text-center p-4 text-gray-500 text-sm bg-gray-50 rounded-lg border border-gray-100">{t('No expenses recorded for this day.')}</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Reconciliation & Closing */}
                            <div className="space-y-6">
                                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                        <span>💵</span> {t('Cash Drawer Reconciliation')}
                                    </h3>
                                    
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
                                        <div className="flex justify-between items-center text-gray-700">
                                            <span className="font-semibold">{t('Expected Cash')}</span>
                                            <span className="font-bold text-lg">{formatCurrency(closingData.expected_cash)}</span>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-1">{t('Actual Cash Counted')} *</label>
                                            <input 
                                                type="number" 
                                                disabled={dayStatus === 'Closed'}
                                                step="0.01" 
                                                value={actualCash} 
                                                onChange={e => setActualCash(e.target.value)} 
                                                className="w-full border-2 border-indigo-200 focus:border-indigo-500 p-2 rounded text-lg font-bold" 
                                                placeholder="0.00" 
                                            />
                                        </div>

                                        <div className={`flex justify-between items-center p-3 rounded-lg ${
                                            (actualCash && (actualCash - closingData.expected_cash) !== 0) 
                                                ? 'bg-red-50 text-red-800 border border-red-200' 
                                                : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                        }`}>
                                            <span className="font-semibold">{t('Difference')}</span>
                                            <span className="font-bold text-lg">
                                                {formatCurrency(actualCash ? (actualCash - closingData.expected_cash) : 0)}
                                            </span>
                                        </div>

                                        {(actualCash && (actualCash - closingData.expected_cash) !== 0) && (
                                            <div>
                                                <label className="block text-sm font-bold text-red-700 mb-1">{t('Reason for Difference')} *</label>
                                                <textarea 
                                                    disabled={dayStatus === 'Closed'}
                                                    value={differenceReason}
                                                    onChange={e => setDifferenceReason(e.target.value)}
                                                    className="w-full border-2 border-red-200 p-2 rounded text-sm focus:border-red-500" 
                                                    placeholder={t('Explain shortage or overage')}
                                                    rows="2"
                                                ></textarea>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {isEndOfDay && dayStatus === 'Open' && (user?.role === 'Admin' || user?.role === 'Manager') && (
                                        <button 
                                            onClick={handleCloseDay}
                                            className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white font-bold py-4 rounded-lg shadow-lg uppercase tracking-wider flex justify-center items-center gap-2 transition"
                                        >
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                                            {t('Close Day')}
                                        </button>
                                    )}

                                    {isEndOfDay && dayStatus === 'Open' && user?.role === 'Cashier' && (
                                        <div className="mt-6 text-center text-sm font-semibold text-red-500 p-3 bg-red-50 rounded-lg">
                                            {t('Only managers can officially close the day. Your entries are saved.')}
                                        </div>
                                    )}

                                </div>
                            </div>

                        </div>
                    ) : null}
                </>
            )}

            {activeTab === 'History' && (
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    {loadingHistory ? (
                        <div className="flex justify-center p-10"><span className="animate-spin text-4xl">⏳</span></div>
                    ) : (
                        <DataTable columns={historyColumns} data={history} />
                    )}
                </div>
            )}

            {/* Expense Modal */}
            {isExpenseOpen && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={e => { if (e.target===e.currentTarget) setIsExpenseOpen(false); }}>
                    <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:600, maxHeight: '90vh', overflowY: 'auto', boxShadow:'0 24px 80px rgba(0,0,0,.3)' }}>
                        <div style={{ padding:'20px 24px', borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center', position: 'sticky', top: 0, background: '#fff', zIndex: 10 }}>
                            <div style={{ fontWeight:800, fontSize:18, color:'#1e293b' }}>{t('Bulk Expenses (Dépenses)')}</div>
                            <button onClick={() => setIsExpenseOpen(false)} style={{ background:'transparent', border:'none', fontSize:24, cursor:'pointer', color:'#64748b' }}>&times;</button>
                        </div>
                        <form onSubmit={handleSaveExpense} style={{ padding:24 }}>
                            <div style={{ marginBottom: 24, padding: '16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                <label style={{ display:'block', fontSize:14, fontWeight:700, marginBottom:8, color:'#334155' }}>{t('Select Cashier (Optional)')}</label>
                                <select value={expenseCashier} onChange={e => setExpenseCashier(e.target.value)} style={{ width:'100%', padding:'12px', border:'1px solid #cbd5e1', borderRadius:8, outline:'none', background: '#fff' }}>
                                    <option value="">-- {t('General / Not Assigned')} --</option>
                                    {cashiersList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>

                            <div style={{ spaceY: 16 }}>
                                {expenseRows.map((row, index) => (
                                    <div key={index} style={{ marginBottom: 16, padding: '16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, position: 'relative' }}>
                                        <div style={{ fontWeight: 800, fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>{t('Expense Layer')} {index + 1}</div>
                                        {expenseRows.length > 1 && (
                                            <button type="button" onClick={() => setExpenseRows(expenseRows.filter((_, i) => i !== index))} style={{ position: 'absolute', top: 12, right: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>{t('Remove')}</button>
                                        )}
                                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
                                            <div>
                                                <label style={{ display:'block', fontSize:12, fontWeight:700, marginBottom:6, color:'#475569' }}>{t('Category / Name')} *</label>
                                                <select 
                                                    required 
                                                    value={row.category} 
                                                    onChange={e => { 
                                                        const newRows = [...expenseRows]; 
                                                        if (e.target.value === '__ADD_NEW__') {
                                                            const newCat = window.prompt(t('Enter new expense category name:'));
                                                            if (newCat) {
                                                                authFetch('/api/accounts', { method: 'POST', body: JSON.stringify({ code: '', name: newCat, type: 'Expense' }) })
                                                                    .then(res => res.json())
                                                                    .then(() => {
                                                                        setAccounts([...accounts, { name: newCat, type: 'Expense' }]);
                                                                        newRows[index].category = newCat;
                                                                        setExpenseRows([...newRows]);
                                                                    });
                                                            }
                                                        } else {
                                                            newRows[index].category = e.target.value; 
                                                            setExpenseRows(newRows);
                                                        }
                                                    }} 
                                                    style={{ width:'100%', padding:'10px', border:'1px solid #cbd5e1', borderRadius:8, outline:'none', background: '#fff' }}
                                                >
                                                    <option value="">-- {t('Select Category')} --</option>
                                                    {accounts.filter(a => a.type === 'Expense').map(a => <option key={a.id || a.name} value={a.name}>{a.name}</option>)}
                                                    {row.category && !accounts.some(a => a.type === 'Expense' && a.name === row.category) && (
                                                        <option value={row.category}>{row.category}</option>
                                                    )}
                                                    <option value="__ADD_NEW__">+ {t('Add New Category')}</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label style={{ display:'block', fontSize:12, fontWeight:700, marginBottom:6, color:'#475569' }}>{t('Amount')} *</label>
                                                <input required type="number" step="0.01" min="0.01" value={row.amount} onChange={e => { const newRows = [...expenseRows]; newRows[index].amount = e.target.value; setExpenseRows(newRows); }} style={{ width:'100%', padding:'10px', border:'1px solid #cbd5e1', borderRadius:8, outline:'none' }} />
                                            </div>
                                        </div>
                                        <div>
                                            <label style={{ display:'block', fontSize:12, fontWeight:700, marginBottom:6, color:'#475569' }}>{t('Description (Optional)')}</label>
                                            <input value={row.description} onChange={e => { const newRows = [...expenseRows]; newRows[index].description = e.target.value; setExpenseRows(newRows); }} style={{ width:'100%', padding:'10px', border:'1px solid #cbd5e1', borderRadius:8, outline:'none' }} placeholder={t('Extra details...')} />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button type="button" onClick={() => setExpenseRows([...expenseRows, { category: '', amount: '', description: '' }])} style={{ width: '100%', padding: '12px', border: '2px dashed #cbd5e1', background: 'transparent', color: '#64748b', fontWeight: 'bold', borderRadius: 8, marginBottom: 24, cursor: 'pointer' }}>
                                + {t('Add Another Layer')}
                            </button>

                            <button type="submit" style={{ width:'100%', background:'#ea580c', color:'#fff', padding:'14px', border:'none', borderRadius:8, fontWeight:800, fontSize: 16, cursor:'pointer', boxShadow:'0 4px 12px rgba(234, 88, 12, 0.2)' }}>{t('Save All Expenses')}</button>
                        </form>
                    </div>
                </div>
            )}
            {/* History Details Modal */}
            {viewingHistoryRecord && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={e => { if (e.target===e.currentTarget) setViewingHistoryRecord(null); }}>
                    <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:800, maxHeight: '90vh', overflowY: 'auto', boxShadow:'0 24px 80px rgba(0,0,0,.3)' }}>
                        <div style={{ padding:'20px 24px', borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center', position: 'sticky', top: 0, background: '#fff', zIndex: 10 }}>
                            <div style={{ fontWeight:800, fontSize:18, color:'#1e293b' }}>
                                {t('Day Summary')}: {viewingHistoryRecord.closure_date}
                            </div>
                            <button onClick={() => setViewingHistoryRecord(null)} style={{ background:'transparent', border:'none', fontSize:24, cursor:'pointer', color:'#64748b' }}>&times;</button>
                        </div>
                        <div style={{ padding:24 }}>
                            {/* Summary Stats */}
                            {historyDetails && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
                                    <div style={{ padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>{t('Net Sales')}</div>
                                        <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{formatCurrency(historyDetails.net_sales)}</div>
                                    </div>
                                    <div style={{ padding: 16, background: '#fff1f2', borderRadius: 8, border: '1px solid #fecdd3' }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#f43f5e', marginBottom: 4 }}>{t('Total Expenses')}</div>
                                        <div style={{ fontSize: 20, fontWeight: 800, color: '#9f1239' }}>{formatCurrency(historyDetails.expense_total)}</div>
                                    </div>
                                    <div style={{ padding: 16, background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#22c55e', marginBottom: 4 }}>{t('Expected Cash')}</div>
                                        <div style={{ fontSize: 20, fontWeight: 800, color: '#166534' }}>{formatCurrency(historyDetails.expected_cash)}</div>
                                    </div>
                                    <div style={{ padding: 16, background: '#fff7ed', borderRadius: 8, border: '1px solid #fed7aa' }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#f97316', marginBottom: 4 }}>{t('Total Purchases')}</div>
                                        <div style={{ fontSize: 20, fontWeight: 800, color: '#9a3412' }}>{formatCurrency(historyDetails.achat_total)}</div>
                                    </div>
                                </div>
                            )}

                            {/* Expenses Section */}
                            <div style={{ marginBottom: 32 }}>
                                <h3 style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', marginBottom: 12, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>{t('Expenses (Dépenses)')}</h3>
                                {historyExpenses.length === 0 ? (
                                    <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 14 }}>{t('No expenses recorded.')}</div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {historyExpenses.map(exp => (
                                            <div key={exp.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
                                                {editingHistoryExpense?.id === exp.id ? (
                                                    <form onSubmit={handleUpdateHistoryExpense} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr auto', gap: 12, alignItems: 'end' }}>
                                                        <div>
                                                            <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block' }}>Category</label>
                                                            <select 
                                                                required 
                                                                value={editingHistoryExpense.category} 
                                                                onChange={e => {
                                                                    if (e.target.value === '__ADD_NEW__') {
                                                                        const newCat = window.prompt(t('Enter new expense category name:'));
                                                                        if (newCat) {
                                                                            authFetch('/api/accounts', { method: 'POST', body: JSON.stringify({ code: '', name: newCat, type: 'Expense' }) })
                                                                                .then(res => res.json())
                                                                                .then(() => {
                                                                                    setAccounts([...accounts, { name: newCat, type: 'Expense' }]);
                                                                                    setEditingHistoryExpense({...editingHistoryExpense, category: newCat});
                                                                                });
                                                                        }
                                                                    } else {
                                                                        setEditingHistoryExpense({...editingHistoryExpense, category: e.target.value});
                                                                    }
                                                                }} 
                                                                style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff' }}
                                                            >
                                                                <option value="">-- Select --</option>
                                                                {accounts.filter(a => a.type === 'Expense').map(a => <option key={a.id || a.name} value={a.name}>{a.name}</option>)}
                                                                {editingHistoryExpense.category && !accounts.some(a => a.type === 'Expense' && a.name === editingHistoryExpense.category) && (
                                                                    <option value={editingHistoryExpense.category}>{editingHistoryExpense.category}</option>
                                                                )}
                                                                <option value="__ADD_NEW__">+ Add New</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block' }}>Amount</label>
                                                            <input required type="number" step="0.01" value={editingHistoryExpense.amount} onChange={e => setEditingHistoryExpense({...editingHistoryExpense, amount: e.target.value})} style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6 }} />
                                                        </div>
                                                        <div>
                                                            <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block' }}>Description</label>
                                                            <input value={editingHistoryExpense.description || ''} onChange={e => setEditingHistoryExpense({...editingHistoryExpense, description: e.target.value})} style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6 }} />
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            <button type="submit" style={{ background: '#22c55e', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>Save</button>
                                                            <button type="button" onClick={() => setEditingHistoryExpense(null)} style={{ background: '#94a3b8', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                                                        </div>
                                                    </form>
                                                ) : (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <div>
                                                            <div style={{ fontWeight: 700, color: '#334155' }}>
                                                                {exp.category} 
                                                                {exp.cashier_name && <span style={{ marginLeft: 8, fontSize: 10, background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: 4 }}>{exp.cashier_name}</span>}
                                                            </div>
                                                            {exp.description && <div style={{ fontSize: 12, color: '#64748b' }}>{exp.description}</div>}
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                                            <div style={{ fontWeight: 800, color: '#0f172a' }}>{formatCurrency(exp.amount)}</div>
                                                            <button onClick={() => setEditingHistoryExpense(exp)} style={{ background: '#f1f5f9', border: 'none', padding: '6px 12px', borderRadius: 6, color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                                                {t('Edit')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Purchases Section */}
                            <div>
                                <h3 style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', marginBottom: 12, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>{t('Purchases (Achats)')}</h3>
                                {historyPurchases.length === 0 ? (
                                    <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 14 }}>{t('No purchases recorded.')}</div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {historyPurchases.map(pur => (
                                            <div key={pur.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
                                                <div>
                                                    <div style={{ fontWeight: 700, color: '#334155' }}>{pur.supplier || pur.supplier_name || 'Local Purchase'}</div>
                                                    <div style={{ fontSize: 12, color: '#64748b' }}>Ref: {pur.reference_number || pur.invoice_number || `#${pur.id}`}</div>
                                                </div>
                                                <div style={{ fontWeight: 800, color: '#ea580c' }}>
                                                    {formatCurrency(pur.grand_total || 0)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DailySalesClosing;
